import type {
  EditableVariant,
  ProductFormData,
  ProductPriceType,
} from './productFormTypes';
import { SERVICE_HOUR_UNIT } from '../../../utils/productKind';

/**
 * Canonical create-product payload extracted from NewProduct.tsx
 * (`createProductPayload`). Field-for-field identical to the pre-refactor
 * inline builder plus `bp_product_kind`; snapshot tests pin the shape.
 * A service sends the same keys with its stock fields blank.
 */
export const buildCreateProductPayload = (params: {
  formData: ProductFormData;
  priceType: ProductPriceType | string;
  hasVariants: boolean;
  variantAttributeNames: string[];
  activeVariantRows: EditableVariant[];
  isService?: boolean;
}) => {
  const {
    formData,
    priceType,
    hasVariants,
    variantAttributeNames,
    activeVariantRows,
    isService = false,
  } = params;

  const variantsPayload = hasVariants
    ? activeVariantRows.map(variant => {
        const attributes: Record<string, string> = {};
        variantAttributeNames.forEach(attributeName => {
          attributes[attributeName] = String(
            variant.bp_variant_attributes?.[attributeName] || '',
          );
        });
        return {
          bp_variant_sku: String(variant.bp_variant_sku || ''),
          bp_variant_attributes: attributes,
          bp_variant_barcode: String((variant.bp_variant_barcode || '').trim()),
          bp_variant_buying_price: Number(variant.bp_variant_buying_price || 0),
          bp_variant_selling_price: Number(
            variant.bp_variant_selling_price || 0,
          ),
          bp_variant_quantity: Number(variant.bp_variant_quantity || 0),
          bp_variant_image: String(variant.bp_variant_image || ''),
          bp_variant_minimum_stock_level: Number(
            variant.bp_variant_minimum_stock_level || 0,
          ),
          bp_variant_display_name: String(
            variant.bp_variant_display_name || '',
          ),
        };
      })
    : [];

  // Create-form UI no longer collects mass for item products (size goes in the
  // name). Sell-per-unit still collects unit-of-mass only. Always send the same
  // API keys — empty string when not applicable — so this is not a breaking change.
  const isUnitPrice = String(priceType) === 'unit';
  // A per-hour service is priced per unit, in hours
  const unitOfMass = isService
    ? isUnitPrice
      ? SERVICE_HOUR_UNIT
      : ''
    : isUnitPrice
      ? String(formData.unitofmass || '')
      : '';
  const mass = '';
  // A service keeps no stock: quantity, minimum level and barcode stay blank
  const noStock = hasVariants || isService;

  return {
    bp_products_information: [
      {
        bp_product_name: formData.productname,
        bp_product_category: formData.category,
        // Sub-category removed from UI; always send empty string on create
        bp_product_sub_category: '',
        bp_product_unit_of_mass: unitOfMass,
        bp_product_buying_price: hasVariants ? '' : formData.buyingprice,
        bp_product_selling_price: hasVariants ? '' : formData.sellingprice,
        bp_product_quantity: noStock ? '' : formData.quantity,
        bp_product_mass: mass,
        bp_product_size: isService ? '' : formData.size,
        bp_product_model: isService ? '' : formData.model,
        bp_minimum_stock_level: noStock ? '' : formData.msl,
        bp_product_price_type: priceType,
        bp_product_kind: isService ? 'service' : 'product',
        bp_product_barcode: noStock ? '' : (formData.barcode || '').trim(),
        bp_product_image: String(
          formData.bp_product_image ||
            (hasVariants
              ? String(
                  activeVariantRows.find(v => v.bp_variant_image)
                    ?.bp_variant_image || '',
                )
              : ''),
        ),
        bp_product_has_variants: hasVariants,
        bp_product_variant_attributes: hasVariants ? variantAttributeNames : [],
        bp_product_variants: hasVariants ? variantsPayload : [],
      },
    ],
  };
};

export type CreateProductPayload = ReturnType<typeof buildCreateProductPayload>;
